<template>
    <!-- UApp is mandatory: toasts, tooltips and programmatic overlays do not work
         without it, and it renders the toaster itself — there is no <Toast> to mount. -->
    <UApp :locale="uiLocale" :toaster="{ position: 'top-right' }">
        <!-- The layout renders the page itself: `templates/shell/` Default.vue and Blank.vue
             each carry their own `<router-view />` and take no slot. Nothing is drawn until the
             first navigation has resolved, or the sign-in page would open with a flash of the
             signed-in shell — and, signed in, until the person is known: the menu and whether
             this page is theirs at all depend on it. A public page never waits for anybody. -->
        <component :is="layout" v-if="drawn" />

        <!-- Something is being saved: a hairline across the top rather than a curtain. -->
        <div
            v-if="appStore.isLoaderActive() && !isPublic"
            class="fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden bg-primary/20"
            role="progressbar"
            :aria-label="$t('general.saving')"
        >
            <div class="h-full w-1/3 animate-pulse bg-primary" />
        </div>
    </UApp>
</template>

<script lang="ts" setup>
    import { computed, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute, useRouter } from 'vue-router'

    import { INTL_LOCALE, Locale } from '@/enums/locale'
    import Blank   from '@/layouts/Blank.vue'
    import Default from '@/layouts/Default.vue'
    import { accessRedirect } from '@/libs/access'
    import { uiLocaleFor } from '@/libs/i18n/uiLocale'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import { useTenantStore } from '@/stores/tenant'
    import type { IMe } from '@/types/User'

    const route       = useRoute()
    const router      = useRouter()
    const appStore    = useAppStore()
    const fetchStore  = useFetchStore()
    const tenantStore = useTenantStore()
    const { locale }  = useI18n()

    // Keeps Nuxt UI's built-in component texts (the calendar, "no data") in the reader's
    // language (`libs/i18n/uiLocale.ts`).
    const uiLocale = computed(() => uiLocaleFor(locale.value))

    const routerReady = ref(false)
    const meFailed    = ref(false)

    // A failed first navigation still draws the shell rather than an empty window.
    void router.isReady().catch(() => undefined).finally(() => { routerReady.value = true })

    const layouts  = { Default, Blank }
    const layout   = computed(() => layouts[route.meta.layout ?? 'Default'])
    const isBlank  = computed(() => route.meta.layout === 'Blank')
    /** A public page (the sign-in page): nobody's session is asked about. */
    const isPublic = computed(() => route.meta.requiresAuth === false)

    /*
     * WHEN THE PAGE MAY BE DRAWN. Signed out, on a public page or on a Blank one, at once.
     * Signed in, once GET /me has said who this is and the page turned out to be theirs. Should
     * /me fail for any other reason than an expired session, the console is drawn anyway rather
     * than left blank. It latches: once drawn, the shell stays.
     */
    const drawn = ref(false)

    watch(
        () => routerReady.value && (
            !appStore.isLoggedIn()
            || isBlank.value
            || isPublic.value
            || meFailed.value
            || (!!appStore.userData && !accessRedirect(route))
        ),
        (ready) => { if (ready) drawn.value = true },
        { immediate: true },
    )

    /*
     * Who is signed in is asked of the API, once per session: the token is the API's to
     * interpret. A login answers it itself (`user` in the answer), so this only runs on a
     * reload with a stored token — and never on a public page.
     */
    let asking = false

    const loadMe = async () => {
        if (asking) return

        asking         = true
        meFailed.value = false

        const { statusCode, data } = await fetchStore.fetch('/me').get().json<IMe>()

        if (statusCode.value === 200 && data.value?.id) appStore.setUserData(data.value)
        else if (appStore.isLoggedIn()) meFailed.value = true

        asking = false
    }

    watch(
        () => [routerReady.value, appStore.JWT, isPublic.value] as const,
        ([ready, jwt, open]) => {
            if (ready && jwt && !open && !appStore.userData) void loadMe()
            if (!jwt) tenantStore.clear()
        },
        { immediate: true },
    )

    /*
     * The person is known: a page that is not theirs — opened from an old link before they were
     * known — gives way (`libs/access.ts`). The router's guard makes the same check on every
     * later navigation.
     */
    watch(() => appStore.userData, (me) => {
        if (!me) return

        const redirect = accessRedirect(route)

        if (redirect) void router.replace(redirect)
    })

    /*
     * THE TENANT OF THE ADDRESS becomes the current tenant (`stores/tenant.ts`): every tenant
     * page is `/t/:tid/…`, and the chrome (its name, the provider's selector) and every canvas
     * (its brand) follow it.
     */
    watch(
        () => [route.params.tid, appStore.userData?.id] as const,
        ([tid, userId]) => {
            if (typeof tid === 'string' && tid && userId && route.meta.tenant) void tenantStore.select(tid)
        },
        { immediate: true },
    )

    watch(locale, (value) => {
        document.documentElement.lang = INTL_LOCALE[value as Locale] ?? value
    }, { immediate: true })
</script>
